import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';

interface FiatExchangeRateRequst {
  subject: Observable<number>,
  fromCurrency: string,
  toCurrency: string,
  timestamp: number
}

@Injectable({
  providedIn: 'root',
})
export class FiatExchangeRateService {
  static readonly CACHE_TTL = 1 * 60 * 1000 // 1 Minute
  private lastRequest: FiatExchangeRateRequst;

  constructor(private api: ApiService) {}

  public getGlobalCurrencyRate(fromCurrency: string): Observable<number> {
    return this.getExchangeRate(fromCurrency, environment.currencies.globalCurrency);
  }

  public getExchangeRate(fromCurrency: string, toCurrency: string, forceRefresh = false): Observable<number> {
    if (!forceRefresh && this.lastRequest?.subject && this.isValidCache(fromCurrency, toCurrency)) {
      return this.lastRequest.subject;
    }

    this.lastRequest = {
      subject: null,
      fromCurrency: fromCurrency,
      toCurrency: toCurrency,
      timestamp: (new Date()).getTime()
    }

    this.lastRequest.subject = this.api.get(`
      query ExchangeFiat($fromCurrency: String!, $toCurrency: String!) {
        exchangeFiat(fromCurrency: $fromCurrency, toCurrency: $toCurrency) {
          rate
        }
      }`,
      {
        fromCurrency,
        toCurrency,
      },
      false
    ).pipe(
      switchMap((response) => {
        if (response['errors']) {
          return throwError(response);
        }
        const exchangeRate = response['data']['exchangeFiat']['rate'];
        return of(parseFloat(exchangeRate));
      }),
      this.api.unifyErrors(),
      shareReplay()
    );

    return this.lastRequest.subject;
  }

  private isValidCache(fromCurrency: string, toCurrency: string): boolean {
    if (
      this.lastRequest &&
      this.lastRequest.fromCurrency === fromCurrency &&
      this.lastRequest.toCurrency === toCurrency &&
      this.lastRequest.timestamp &&
      this.lastRequest.subject) {
      const currentTime = (new Date()).getTime();
      return currentTime < this.lastRequest.timestamp + FiatExchangeRateService.CACHE_TTL;
    }

    return false;
  }
}
